import React, { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  styled,
  CircularProgress,
  Alert,
} from '@mui/material'
import Save from '@mui/icons-material/Save'
import LocalHospital from '@mui/icons-material/LocalHospital'
import { postCPDataWithParams } from 'src/clinic/services'
import { useVitalsForm } from 'src/reducers/vitalsFormSettings/hooks/useVitalsForm'
import { toast } from 'react-toastify'

const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  border: '1px solid rgba(67, 56, 202, 0.15)',
  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.02) 0%, rgba(99, 102, 241, 0.02) 100%)',
  marginBottom: '16px',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: 'rgba(67, 56, 202, 0.3)',
    boxShadow: '0 4px 12px rgba(67, 56, 202, 0.08)',
  },
}))

const VitalLabel = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 600,
  color: 'rgba(0, 0, 0, 0.7)',
  marginBottom: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}))

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '&:hover fieldset': {
      borderColor: 'rgba(67, 56, 202, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4338ca',
    },
  },
}))

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  color: '#fff',
  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.9) 0%, rgba(99, 102, 241, 0.9) 100%)',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%)',
  },
  '&.Mui-disabled': {
    background: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
  },
}))

export default function AddVitalsContent({
  bookedSlot,
  mentorProfileData,
  clinicLocationData,
  refreshBookedSlots,
  vitalsFormGetApiSignature: providedVitalsFormGetApiSignature,
}) {
  const [saving, setSaving] = useState(false)
  const patientId = bookedSlot?.patientId
  const clinicId = bookedSlot?.clinicId
  const bookedEventId = bookedSlot?.id
  const practitionerId = mentorProfileData?.userId

  // Use provided API signature or construct based on portal/role
  const vitalsFormGetApiSignature = (() => {
    if (providedVitalsFormGetApiSignature) {
      // If it's the practitioner API, use as-is (no params needed)
      if (providedVitalsFormGetApiSignature.includes('/practitioner/vitals/form')) {
        return providedVitalsFormGetApiSignature
      }
      
      // For clinic-assistant API, add params if not already present
      if (providedVitalsFormGetApiSignature.includes('/clinic-assistant/vitals/form')) {
        // Check if params are already in the URL
        if (providedVitalsFormGetApiSignature.includes('?')) {
          return providedVitalsFormGetApiSignature
        }
        return `${providedVitalsFormGetApiSignature}?practitionerId=${practitionerId}&clinicLocationId=${clinicId}`
      }
      
      // Return as-is if it doesn't match known patterns
      return providedVitalsFormGetApiSignature
    }
    
    // Fallback: construct clinic-assistant API (for backward compatibility)
    return `/api/secure/clinic-assistant/vitals/form?practitionerId=${practitionerId}&clinicLocationId=${clinicId}`
  })()

  // Use centralized vitals hook with all the complex logic
  const {
    vitalsFields,
    vitalData,
    selectedUnits,
    hasData,
    handleInputChange,
    handleUnitChange,
    getDisplayValue,
    getDefaultUnit,
  } = useVitalsForm({ vitalsFormGetApiSignature: vitalsFormGetApiSignature })

  const saveVital = async () => {
    if (!hasData) {
      toast.info('Please enter at least one vital measurement')
      return
    }

    setSaving(true)
    const toSendData = { ...vitalData, patientId, clinicId }
    if (bookedEventId) {
      toSendData.bookedEventId = bookedEventId
    }

    try {
      await postCPDataWithParams('/api/secure/patient/vitals/save', toSendData, {})
      toast.success('Vitals saved successfully')
      if (refreshBookedSlots) {
        await refreshBookedSlots()
      }
    } catch (error) {
      console.error('Failed to save vitals:', error)
      toast.error('Failed to save vitals')
    } finally {
      setSaving(false)
    }
  }

  const renderVitalField = (field) => {
    if (!field || field.hidden || !field.id) return null

    const value = getDisplayValue(field.id) || ''
    const unit = selectedUnits[field.id] || getDefaultUnit(field)
    const displayName = field.displayName || field.id || 'Field'

    // Handle textarea fields
    if (field.inputType === 'textarea') {
      return (
        <Grid item xs={12} key={field.id}>
          <ModernCard>
            <CardContent>
              <VitalLabel>
                <LocalHospital sx={{ fontSize: 16, color: '#4338ca' }} />
                {displayName}
                {field.required && <span style={{ color: '#dc2626' }}>*</span>}
              </VitalLabel>
              <StyledTextField
                fullWidth
                multiline
                rows={3}
                placeholder={`Enter ${displayName.toLowerCase()}`}
                value={value}
                onChange={(e) => handleInputChange(field.id, e.target.value)}
                disabled={field.disabled}
              />
            </CardContent>
          </ModernCard>
        </Grid>
      )
    }

    // Handle regular input fields
    return (
      <Grid item xs={12} sm={6} key={field.id}>
        <ModernCard>
          <CardContent>
            <VitalLabel>
              <LocalHospital sx={{ fontSize: 16, color: '#4338ca' }} />
              {displayName}
              {field.required && <span style={{ color: '#dc2626' }}>*</span>}
            </VitalLabel>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
              <StyledTextField
                fullWidth
                type={field.inputType === 'number' ? 'number' : 'text'}
                placeholder={`Enter ${displayName.toLowerCase()}`}
                value={value}
                onChange={(e) => handleInputChange(field.id, e.target.value)}
                disabled={field.disabled}
                InputProps={
                  !field.availableUnits || field.availableUnits.length === 0
                    ? {
                        endAdornment: field.defaultUnit && (
                          <InputAdornment position="end">
                            <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                              {field.defaultUnit}
                            </Typography>
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
              {field.availableUnits && field.availableUnits.length > 0 && (
                <FormControl sx={{ minWidth: 80 }}>
                  <Select
                    value={unit}
                    onChange={(e) => handleUnitChange(field.id, e.target.value)}
                    size="small"
                    sx={{
                      borderRadius: '8px',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(67, 56, 202, 0.5)',
                        },
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4338ca',
                      },
                    }}
                  >
                    {field.availableUnits.map((unitOption) => (
                      <MenuItem key={unitOption} value={unitOption}>
                        {unitOption}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </CardContent>
        </ModernCard>
      </Grid>
    )
  }

  if (!vitalsFields || vitalsFields.length === 0) {
    return (
      <Box sx={{ padding: 2 }}>
        <Alert severity="info">Loading vitals form...</Alert>
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: '#4338ca', marginBottom: '8px' }}
        >
          Record Patient Vitals
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
          Enter the patient's vital measurements. Fields marked with * are required.
        </Typography>
      </Box>

      {/* Vitals Grid */}
      <Grid container spacing={2}>
        {vitalsFields.map(renderVitalField).filter(Boolean)}
      </Grid>

      {/* Save Button */}
      <Box 
        sx={{ 
          marginTop: 3, 
          paddingTop: 2,
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          display: 'flex', 
          justifyContent: 'flex-end' 
        }}
      >
        <StyledButton
          variant="contained"
          onClick={saveVital}
          disabled={saving || !hasData}
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: '200px' },
          }}
        >
          {saving ? 'Saving...' : 'Save Vitals'}
        </StyledButton>
      </Box>
    </Box>
  )
}

